import { Box, Flex, IconButton, Text, VStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RiDeleteBin6Line, RiEditLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  listCriteriaSets,
  type CriteriaSet,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import { useEnrollmentPeriodContext } from "../../Edit";
import { AddCriteriaSetLink } from "./AddCriteriaSetLink";
import {
  EntityConditionsDescription,
  InheritedConditionsDescriptions,
  PanelLoading,
} from "./ConditionsDescription";
import { type CriterionForEntity } from "./criterionForEntity";

type Props = {
  enrollmentPeriodId: string;
  criterionForEntity: CriterionForEntity;
  isOpen: boolean;
  onEditCriteriaSet: (criteriaSet: CriteriaSet) => void;
};

export function CriteriaSettingsScreen({
  enrollmentPeriodId,
  criterionForEntity,
  isOpen,
  onEditCriteriaSet,
}: Props) {
  const criteriaSetsRD = useRemoteDataRestQuery({
    queryKey: ["ListCriteriaSets", enrollmentPeriodId],
    enabled: isOpen,
    queryFn: () =>
      listCriteriaSets({ path: { enrollmentPeriodId } }).then(
        (data) => data.data?.data ?? []
      ),
  });

  return (
    <ResizableDrawer.Content>
      <ResizableDrawer.Header>Criteria settings</ResizableDrawer.Header>
      <ResizableDrawer.Body>
        <VStack align="stretch" spacing={6}>
          {criterionForEntity.description()}

          <RemoteDataView
            remoteData={criteriaSetsRD}
            loading={<PanelLoading />}
            error={(error) => <GenericError error={error} />}
          >
            {(sets) => (
              <VStack align="stretch" spacing={4}>
                {sets.map((criteriaSet) => (
                  <CriteriaSetCard
                    key={criteriaSet.id}
                    criteriaSet={criteriaSet}
                    criterionForEntity={criterionForEntity}
                    isOpen={isOpen}
                    onEdit={() => onEditCriteriaSet(criteriaSet)}
                  />
                ))}
                <AddCriteriaSetLink
                  enrollmentPeriodId={enrollmentPeriodId}
                  hasCriteriaSets={sets.length > 0}
                  existingKeys={sets.map((set) => set.key)}
                />
              </VStack>
            )}
          </RemoteDataView>
        </VStack>
      </ResizableDrawer.Body>
    </ResizableDrawer.Content>
  );
}

function CriteriaSetCard<T extends CriterionForEntity>({
  criteriaSet,
  criterionForEntity,
  isOpen,
  onEdit,
}: {
  criteriaSet: CriteriaSet;
  criterionForEntity: T;
  isOpen: boolean;
  onEdit: () => void;
}) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const { enrollmentPeriod } = useEnrollmentPeriodContext();
  const useSeatTypes = enrollmentPeriod.use_service_offering_seat_types;
  const [isDeleting, setIsDeleting] = React.useState(false);
  const criterionRD = useRemoteDataRestQuery({
    queryKey: criterionForEntity.queryKey(criteriaSet.id),
    enabled: isOpen,
    queryFn: () => criterionForEntity.fetch(criteriaSet.id),
  });
  const inheritedSources = criterionForEntity.inheritedSources ?? [];

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Delete conditions",
    body: (
      <>
        Delete conditions for <b>{criterionForEntity.entityLabel}</b> in{" "}
        <b>{criteriaSet.name}</b> criteria set? You can't undo this action
        afterwards.
        {inheritedSources.length > 0 && (
          <Text fontStyle="italic" mt="2">
            Note: This will reset to inherited conditions, which won't be
            deleted.
          </Text>
        )}
      </>
    ),
    cancelButton: { label: "Cancel" },
    confirmButton: { label: "Yes, delete" },
  });

  const handleDelete = async () => {
    if (!(await confirm())) return;

    setIsDeleting(true);
    try {
      await criterionForEntity.delete(criteriaSet.id);
      toast({
        id: "delete-criterion",
        title: "Conditions deleted",
      });
      await queryClient.invalidateQueries({
        queryKey: criterionForEntity.queryKey(criteriaSet.id),
      });
    } catch (err) {
      console.error(err);
      toast.error({
        id: "delete-criterion",
        title: "Failed to delete conditions",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      p={4}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
    >
      {confirmationDialog}
      <Flex align="flex-start" gap={3}>
        <Box flex="1" minW={0}>
          <Text fontSize="md" fontWeight="medium" color="blackAlpha.800">
            {criteriaSet.name}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {criteriaSet.key}
          </Text>
        </Box>
        <Flex align="center" gap={2} ml="auto" flexShrink={0}>
          <RemoteDataView
            remoteData={criterionRD}
            // Allow deletion if there's an error with the criterion
            error={() => (
              <DeleteConditionsButton
                criteriaSetName={criteriaSet.name}
                isDeleting={isDeleting}
                onDelete={() => void handleDelete()}
              />
            )}
          >
            {(loaded) => (
              <>
                <IconButton
                  aria-label={`Edit ${criteriaSet.name}`}
                  icon={<RiEditLine />}
                  variant="outline"
                  colorScheme="gray"
                  size="sm"
                  onClick={onEdit}
                />
                {loaded.criterion && (
                  <DeleteConditionsButton
                    criteriaSetName={criteriaSet.name}
                    isDeleting={isDeleting}
                    onDelete={() => void handleDelete()}
                  />
                )}
              </>
            )}
          </RemoteDataView>
        </Flex>
      </Flex>
      <RemoteDataView
        remoteData={criterionRD}
        loading={
          <Box mt={4}>
            <PanelLoading />
          </Box>
        }
        error={(error) => (
          <Box mt={4}>
            <GenericError error={error} />
          </Box>
        )}
      >
        {(loaded) => {
          const canInheritConditions = inheritedSources.length > 0;
          const inheritsConditions =
            canInheritConditions &&
            (loaded.criterion?.inheritConditions ?? true);
          return (
            <VStack align="stretch" spacing={4} mt={4}>
              {inheritsConditions ? (
                <InheritedConditionsDescriptions
                  sources={inheritedSources}
                  criteriaSetId={criteriaSet.id}
                  enabled={isOpen}
                />
              ) : canInheritConditions ? (
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  fontStyle="italic"
                  color="gray.600"
                >
                  Not inheriting conditions from the school and options of this{" "}
                  {useSeatTypes ? "seat type" : "offering"}
                </Text>
              ) : null}
              <EntityConditionsDescription
                criterion={loaded}
                hasInheritedSources={inheritedSources.length > 0}
                useSeatTypes={useSeatTypes}
              />
            </VStack>
          );
        }}
      </RemoteDataView>
    </Box>
  );
}

function DeleteConditionsButton({
  criteriaSetName,
  isDeleting,
  onDelete,
}: {
  criteriaSetName: string;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  return (
    <IconButton
      aria-label={`Delete conditions for ${criteriaSetName}`}
      icon={<RiDeleteBin6Line />}
      variant="outline"
      colorScheme="gray"
      size="sm"
      onClick={onDelete}
      isLoading={isDeleting}
    />
  );
}
